/**
 * Renders a single satellite coverage footprint as a translucent polygon, in
 * 2D (google.maps.Polygon) or 3D (Polygon3DElement). In 3D it can also draw a
 * translucent coverage CONE from the satellite shell down to the footprint ring
 * (config-gated). Only one footprint exists at a time — it tracks the selection.
 */

import { CLIENT_CONFIG } from '@/config';
import type { MapInstance, ViewMode } from '../map-setup';

/** Footprint fill/stroke styling. */
const FILL_COLOR = 'rgba(96, 165, 250, 0.18)';
const STROKE_COLOR = 'rgba(125, 200, 255, 0.85)';
const STROKE_WIDTH = 1.5;

/** Cone facet styling (fainter than the ground disk so it reads as volume). */
const CONE_FILL = 'rgba(96, 165, 250, 0.10)';
const CONE_STROKE = 'rgba(125, 200, 255, 0.35)';

/** Number of triangular facets approximating the cone (coarser than the ring). */
const CONE_FACETS = 18;

/** Apex of the coverage cone (sub-satellite point at the displayed shell altitude). */
export interface ConeApex {
  lat: number;
  lng: number;
  altitudeMeters: number;
}

let polygon2d: google.maps.Polygon | null = null;
let polygon3d: google.maps.maps3d.Polygon3DElement | null = null;
let cone3d: google.maps.maps3d.Polygon3DElement[] = [];

/** Draw (replacing any prior) the footprint ring — plus the cone in 3D when enabled. */
export function showFootprint(
  map: MapInstance,
  mode: ViewMode,
  ring: google.maps.LatLngLiteral[],
  cone?: ConeApex,
): void {
  clearFootprint();
  if (mode === '2d') {
    polygon2d = create2d(map as google.maps.Map, ring);
  } else {
    const map3d = map as google.maps.maps3d.Map3DElement;
    polygon3d = create3d(map3d, ring);
    if (cone && CLIENT_CONFIG.coverageConeEnabled) {
      cone3d = createCone(map3d, ring, cone);
    }
  }
}

/** Remove any rendered footprint (and cone) from both renderers. */
export function clearFootprint(): void {
  if (polygon2d) {
    polygon2d.setMap(null);
    polygon2d = null;
  }
  if (polygon3d) {
    polygon3d.remove();
    polygon3d = null;
  }
  for (const facet of cone3d) facet.remove();
  cone3d = [];
}

/** Create a 2D coverage polygon. */
function create2d(map: google.maps.Map, ring: google.maps.LatLngLiteral[]): google.maps.Polygon {
  return new google.maps.Polygon({
    map,
    paths: ring,
    strokeColor: STROKE_COLOR,
    strokeWeight: STROKE_WIDTH,
    fillColor: FILL_COLOR,
    fillOpacity: 1,
    clickable: false,
  });
}

/** Create a 3D coverage polygon clamped to the ground. */
function create3d(
  map3d: google.maps.maps3d.Map3DElement,
  ring: google.maps.LatLngLiteral[],
): google.maps.maps3d.Polygon3DElement {
  const polygon = new google.maps.maps3d.Polygon3DElement({
    outerCoordinates: ring.map((p) => ({ lat: p.lat, lng: p.lng, altitude: 0 })),
    altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    strokeColor: STROKE_COLOR,
    strokeWidth: STROKE_WIDTH,
    fillColor: FILL_COLOR,
    drawsOccludedSegments: true,
  });
  map3d.appendChild(polygon);
  return polygon;
}

/** Build the cone as triangular facets from the apex to a coarse sample of the ring. */
function createCone(
  map3d: google.maps.maps3d.Map3DElement,
  ring: google.maps.LatLngLiteral[],
  apex: ConeApex,
): google.maps.maps3d.Polygon3DElement[] {
  const samples = sampleRing(ring, CONE_FACETS);
  const facets: google.maps.maps3d.Polygon3DElement[] = [];
  for (let i = 0; i < samples.length - 1; i += 1) {
    facets.push(createConeFacet(map3d, apex, samples[i], samples[i + 1]));
  }
  return facets;
}

/** One cone facet: a triangle from the elevated apex down to two adjacent ring points. */
function createConeFacet(
  map3d: google.maps.maps3d.Map3DElement,
  apex: ConeApex,
  a: google.maps.LatLngLiteral,
  b: google.maps.LatLngLiteral,
): google.maps.maps3d.Polygon3DElement {
  const facet = new google.maps.maps3d.Polygon3DElement({
    outerCoordinates: [
      { lat: apex.lat, lng: apex.lng, altitude: apex.altitudeMeters },
      { lat: a.lat, lng: a.lng, altitude: 0 },
      { lat: b.lat, lng: b.lng, altitude: 0 },
    ],
    altitudeMode: google.maps.maps3d.AltitudeMode.ABSOLUTE,
    strokeColor: CONE_STROKE,
    strokeWidth: 1,
    fillColor: CONE_FILL,
    drawsOccludedSegments: false,
  });
  map3d.appendChild(facet);
  return facet;
}

/** Evenly downsample a closed ring to ~`facets`+1 points (keeps endpoints). */
function sampleRing(ring: google.maps.LatLngLiteral[], facets: number): google.maps.LatLngLiteral[] {
  const stride = Math.max(1, Math.floor((ring.length - 1) / facets));
  const sampled: google.maps.LatLngLiteral[] = [];
  for (let i = 0; i < ring.length - 1; i += stride) sampled.push(ring[i]);
  sampled.push(ring[0]);
  return sampled;
}
